import * as Schema from "effect/Schema";
import * as HttpApi from "effect/unstable/httpapi/HttpApi";
import * as HttpApiEndpoint from "effect/unstable/httpapi/HttpApiEndpoint";
import * as HttpApiGroup from "effect/unstable/httpapi/HttpApiGroup";
import * as HttpServerRespondable from "effect/unstable/http/HttpServerRespondable";
import * as HttpServerResponse from "effect/unstable/http/HttpServerResponse";

import { TrimmedNonEmptyString } from "./baseSchemas.ts";
import { CommandId, MessageId, NonNegativeInt, ThreadId, TurnId } from "./baseSchemas.ts";
import { ExecutionEnvironmentDescriptor } from "./environment.ts";
import {
  CommandLineDiscoverySnapshot,
  CommandLineThreadOutputView,
  CommandLineThreadSourceSnapshot,
} from "./commandLineClient.ts";
import {
  ClientOrchestrationCommand,
  DispatchResult,
  EnvironmentCommandReceipt,
  EnvironmentCommandRejectedReceipt,
  OrchestrationReadModel,
} from "./orchestration.ts";
import {
  ManagedThreadWorktreeDeleteInput,
  ManagedThreadWorktreeDeleteResult,
} from "./worktrees.ts";

export const EnvironmentRequestInvalidReason = Schema.Literal("invalid_command");
export type EnvironmentRequestInvalidReason = typeof EnvironmentRequestInvalidReason.Type;

export const EnvironmentInternalErrorReason = Schema.Literals([
  "command_line_discovery_failed",
  "command_line_thread_inspection_failed",
  "command_line_worktree_delete_failed",
  "orchestration_snapshot_failed",
  "orchestration_dispatch_failed",
  "internal_error",
]);
export type EnvironmentInternalErrorReason = typeof EnvironmentInternalErrorReason.Type;

export class EnvironmentRequestInvalidError extends Schema.TaggedErrorClass<EnvironmentRequestInvalidError>()(
  "EnvironmentRequestInvalidError",
  {
    code: Schema.Literal("invalid_request"),
    reason: EnvironmentRequestInvalidReason,
    traceId: TrimmedNonEmptyString,
  },
  { httpApiStatus: 400 },
) {
  [HttpServerRespondable.symbol]() {
    return HttpServerResponse.schemaJson(EnvironmentRequestInvalidError)(this, { status: 400 });
  }
}

export class EnvironmentInternalError extends Schema.TaggedErrorClass<EnvironmentInternalError>()(
  "EnvironmentInternalError",
  {
    code: Schema.Literal("internal_error"),
    reason: EnvironmentInternalErrorReason,
    traceId: TrimmedNonEmptyString,
  },
  { httpApiStatus: 500 },
) {
  [HttpServerRespondable.symbol]() {
    return HttpServerResponse.schemaJson(EnvironmentInternalError)(this, { status: 500 });
  }
}

export class EnvironmentUnauthorizedError extends Schema.TaggedErrorClass<EnvironmentUnauthorizedError>()(
  "EnvironmentUnauthorizedError",
  {
    code: Schema.Literal("unauthorized"),
    reason: Schema.Literal("invalid_environment_access"),
    traceId: TrimmedNonEmptyString,
  },
  { httpApiStatus: 401 },
) {
  [HttpServerRespondable.symbol]() {
    return HttpServerResponse.schemaJson(EnvironmentUnauthorizedError)(this, { status: 401 });
  }
}

export class EnvironmentAccessCommandConflictError extends Schema.TaggedErrorClass<EnvironmentAccessCommandConflictError>()(
  "EnvironmentAccessCommandConflictError",
  {
    code: Schema.Literal("conflict"),
    reason: Schema.Literals(["revision_mismatch", "command_reused"]),
    commandId: CommandId,
    expectedRevision: NonNegativeInt,
    observedRevision: NonNegativeInt,
  },
  { httpApiStatus: 409 },
) {
  [HttpServerRespondable.symbol]() {
    return HttpServerResponse.schemaJson(EnvironmentAccessCommandConflictError)(this, {
      status: 409,
    });
  }
}

export class EnvironmentCommandIdempotencyConflictHttpError extends Schema.TaggedErrorClass<EnvironmentCommandIdempotencyConflictHttpError>()(
  "EnvironmentCommandIdempotencyConflictHttpError",
  {
    code: Schema.Literal("idempotency-conflict"),
    commandId: CommandId,
    existingCommandKind: TrimmedNonEmptyString,
    attemptedCommandKind: TrimmedNonEmptyString,
  },
  { httpApiStatus: 409 },
) {
  [HttpServerRespondable.symbol]() {
    return HttpServerResponse.schemaJson(EnvironmentCommandIdempotencyConflictHttpError)(this, {
      status: 409,
    });
  }
}

export class EnvironmentCommandRejectedHttpError extends Schema.TaggedErrorClass<EnvironmentCommandRejectedHttpError>()(
  "EnvironmentCommandRejectedHttpError",
  { receipt: EnvironmentCommandRejectedReceipt },
  { httpApiStatus: 409 },
) {
  [HttpServerRespondable.symbol]() {
    return HttpServerResponse.schemaJson(EnvironmentCommandRejectedHttpError)(this, {
      status: 409,
    });
  }
}

export const EnvironmentHttpCommonError = Schema.Union([
  EnvironmentRequestInvalidError,
  EnvironmentInternalError,
  EnvironmentUnauthorizedError,
]);
export type EnvironmentHttpCommonError = typeof EnvironmentHttpCommonError.Type;

const EnvironmentOrchestrationSnapshotErrors = [
  EnvironmentUnauthorizedError,
  EnvironmentInternalError,
] as const;
const EnvironmentOrchestrationDispatchErrors = [
  EnvironmentUnauthorizedError,
  EnvironmentRequestInvalidError,
  EnvironmentCommandIdempotencyConflictHttpError,
  EnvironmentCommandRejectedHttpError,
  EnvironmentInternalError,
] as const;

export class EnvironmentMetadataHttpApi extends HttpApiGroup.make("metadata").add(
  HttpApiEndpoint.get("descriptor", "/.well-known/afar-research/environment", {
    success: ExecutionEnvironmentDescriptor,
    error: [EnvironmentUnauthorizedError],
  }),
) {}

export class EnvironmentOrchestrationHttpApi extends HttpApiGroup.make("orchestration")
  .add(
    HttpApiEndpoint.get("snapshot", "/api/orchestration/snapshot", {
      success: OrchestrationReadModel,
      error: EnvironmentOrchestrationSnapshotErrors,
    }),
  )
  .add(
    HttpApiEndpoint.post("dispatch", "/api/orchestration/dispatch", {
      payload: ClientOrchestrationCommand,
      success: DispatchResult,
      error: EnvironmentOrchestrationDispatchErrors,
    }),
  ) {}

export class EnvironmentCommandLineHttpApi extends HttpApiGroup.make("commandLine")
  .add(
    HttpApiEndpoint.get("discovery", "/api/command-line/discovery", {
      success: CommandLineDiscoverySnapshot,
      error: EnvironmentOrchestrationSnapshotErrors,
    }),
  )
  .add(
    HttpApiEndpoint.get("threadList", "/api/command-line/threads", {
      success: CommandLineThreadSourceSnapshot,
      error: EnvironmentOrchestrationSnapshotErrors,
    }),
  )
  .add(
    HttpApiEndpoint.get("threadDetail", "/api/command-line/threads/:threadId", {
      params: { threadId: ThreadId },
      query: {
        view: Schema.optional(CommandLineThreadOutputView),
        turnId: Schema.optional(TurnId),
        messageId: Schema.optional(MessageId),
      },
      success: CommandLineThreadSourceSnapshot,
      error: EnvironmentOrchestrationSnapshotErrors,
    }),
  )
  .add(
    HttpApiEndpoint.post("worktreeDelete", "/api/command-line/worktrees/delete", {
      payload: ManagedThreadWorktreeDeleteInput,
      success: Schema.Struct({
        deletion: ManagedThreadWorktreeDeleteResult,
        receipt: EnvironmentCommandReceipt,
      }),
      error: EnvironmentOrchestrationDispatchErrors,
    }),
  ) {}

export class EnvironmentHttpApi extends HttpApi.make("environment")
  .add(EnvironmentMetadataHttpApi)
  .add(EnvironmentCommandLineHttpApi)
  .add(EnvironmentOrchestrationHttpApi) {}
